/**
 * Serial Profile Management Component
 * Management of Serial Profile
 * Location: Settings → Product → Serial Profile
 */

import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, of, Observable, firstValueFrom } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

// Shared components
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import {
  SearchFormComponent,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  ConfirmationButton,
  ConfirmationDialogComponent,
} from '@app/shared/components/confirmation-dialog/confirmation-dialog.component';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';
import {
  SERIAL_PROFILE_COLUMNS,
  SERIAL_PROFILE_DEFAULT_COLUMNS_ORDER,
  SERIAL_PROFILE_FLEXIBLE_COLUMNS_ORDER,
  SERIAL_PROFILE_ACTION_COLUMN,
} from './serial-profile.config';

// Models and services
import { SerialProfileApiService } from '@app/feature/center-business/goods/spu/services/serial-profile-api.service';
import {
  SerialProfileDialogComponent,
  SerialProfileFormData,
} from './components/serial-profile-dialog/serial-profile-dialog.component';
import { ThemeService } from '@app/core/core';
import { PermissionService } from '@app/core/services/permission.service';

// Types
export type SerialProfileStatus = 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';

export interface SerialProfile {
  id: number;
  name: string;
  description?: string;
  status: SerialProfileStatus;
  serialRequired: boolean;
  lotRequired: boolean;
  expiryRequired: boolean;
  creationDate?: string;
}

export interface SerialProfileSearchParams {
  name?: string;
  description?: string;
  status?: SerialProfileStatus | null;
  requiredField?: 'lotRequired' | 'expiryRequired' | 'serialRequired' | null;
}

@Component({
  selector: 'app-serial-profile',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    AppButtonComponent,
    TagModule,
    AdvancedDataTableComponent,
    SearchFormComponent,
    ToastModule,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService, DialogService],
  templateUrl: './serial-profile.component.html',
  styleUrls: ['./serial-profile.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SerialProfileComponent implements OnInit, OnDestroy {
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  private readonly destroy$ = new Subject<void>();
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly dialogService = inject(DialogService);
  private readonly serialProfileApiService = inject(SerialProfileApiService);
  protected readonly themeService = inject(ThemeService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  private dialogRef: DynamicDialogRef | null = null;

  private readonly confirmResult$ = new Subject<boolean>();

  // Data signals
  profiles = signal<SerialProfile[]>([]);
  filteredProfiles = signal<SerialProfile[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Pagination
  rows = 20;
  page = signal(0);

  // Permission flags - computed from PermissionService
  // Using menu codes: settings.product.serial_profile.view/create/edit
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.product.serial_profile.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.product.serial_profile.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.product.serial_profile.edit');
  });

  // Check if user has any action permissions
  hasAnyActionPermission = computed(() => this.canUpdate());

  // Search form config
  searchFormConfig: FormFieldConfig[] = [
    {
      key: 'name',
      label: 'settings.serial_profile.common.name',
      type: 'text',
      placeholder: 'settings.serial_profile.common.enter_name',
    },
    {
      key: 'description',
      label: 'settings.serial_profile.common.description',
      type: 'text',
      placeholder: 'settings.serial_profile.common.enter_description',
    },
    {
      key: 'status',
      label: 'settings.serial_profile.common.status',
      type: 'select',
      placeholder: 'settings.serial_profile.form.placeholders.select_status',
      options: [
        { label: 'settings.serial_profile.status.active', value: 'ACTIVE' },
        { label: 'settings.serial_profile.status.inactive', value: 'INACTIVE' },
      ],
    },
    {
      key: 'requiredField',
      label: 'settings.serial_profile.requirements.label',
      type: 'select',
      placeholder: 'common.form.placeholders.select',
      options: [
        {
          label: 'settings.serial_profile.requirements.lot',
          value: 'lotRequired',
        },
        {
          label: 'settings.serial_profile.requirements.expiry',
          value: 'expiryRequired',
        },
        {
          label: 'settings.serial_profile.requirements.serial',
          value: 'serialRequired',
        },
      ],
      showClear: true,
    },
  ];

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      SERIAL_PROFILE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of SERIAL_PROFILE_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(SERIAL_PROFILE_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      SERIAL_PROFILE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of SERIAL_PROFILE_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  // Search params
  searchParams = signal<SerialProfileSearchParams>({});

  ngOnInit(): void {
    this.loadProfiles();
  }

  ngOnDestroy(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load all profiles (client-side filtering like Tag Management)
   */
  private loadProfiles(): void {
    this.loading.set(true);
    // Load all profiles without pagination (since data is small)
    this.serialProfileApiService
      .getSerialProfiles() // Load all (filtering handled client-side)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Sort by name
          const sortedData = [...response.data].sort((a, b) =>
            a.name.localeCompare(b.name),
          );
          this.profiles.set(sortedData);
          this.updateFilteredProfiles();

          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading profiles:', error);
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'settings.serial_profile.messages.load_error',
            ),
          });
        },
      });
  }

  /**
   * Update filtered profiles based on search params (client-side filtering)
   */
  private updateFilteredProfiles(): void {
    let filtered = [...this.profiles()];
    const params = this.searchParams();

    // Apply search filters
    if (params.name) {
      filtered = filtered.filter((p) =>
        p.name.toLowerCase().includes(params.name!.toLowerCase()),
      );
    }

    if (params.description) {
      filtered = filtered.filter((p) =>
        p.description
          ?.toLowerCase()
          .includes(params.description!.toLowerCase()),
      );
    }

    // Filter by status
    if (params.status) {
      filtered = filtered.filter((p) => p.status === params.status);
    }

    // Filter by required field (lotRequired, expiryRequired, or serialRequired)
    if (params.requiredField) {
      switch (params.requiredField) {
        case 'lotRequired':
          filtered = filtered.filter((p) => p.lotRequired === true);
          break;
        case 'expiryRequired':
          filtered = filtered.filter((p) => p.expiryRequired === true);
          break;
        case 'serialRequired':
          filtered = filtered.filter((p) => p.serialRequired === true);
          break;
      }
    }

    // Sort by name
    filtered.sort((a, b) => a.name.localeCompare(b.name));
    this.filteredProfiles.set(filtered);
    this.totalRecords.set(filtered.length);
    this.page.set(0); // Reset to first page when filtering
  }

  /**
   * Handle search (client-side filtering)
   */
  onSearch(searchData: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      ...searchData,
    });
    this.updateFilteredProfiles();
  }

  /**
   * Handle reset search
   */
  onReset(): void {
    this.searchParams.set({});
    this.updateFilteredProfiles();
  }

  /**
   * Handle pagination (client-side pagination)
   */
  onPageChange(event: any): void {
    this.page.set(event.page);
    this.rows = event.rows;
  }

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }

  // Date formatting using formatDateTime utility
  formatDateTime(dateInput: any): string {
    return formatDateTime(dateInput);
  }

  /**
   * Handle confirm for changing status to Achieved
   */
  async onConfirm(dialog: SerialProfileDialogComponent) {
    if (this.shouldConfirmArchive(dialog)) {
      this.showArchiveConfirmation(dialog.formData);
      return await firstValueFrom(this.confirmResult$);
    }
    return true;
  }

  /**
   * Open add profile dialog
   */
  onAdd(): void {
    if (!this.canCreate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.dialogRef = this.dialogService.open(SerialProfileDialogComponent, {
      header: this.translateService.instant(
        'settings.serial_profile.dialog.add_title',
      ),
      width: '600px',
      modal: true,
      closable: true,
      data: {},
    });

    this.dialogRef.onClose.subscribe((result: SerialProfileFormData) => {
      if (result) {
        const profileData: Omit<SerialProfile, 'id'> = {
          name: result.name,
          description: result.description,
          lotRequired: result.lotRequired,
          expiryRequired: result.expiryRequired,
          serialRequired: result.serialRequired,
          status: 'ACTIVE',
        };

        this.serialProfileApiService
          .createSerialProfile(profileData)
          .pipe(
            switchMap(() => this.serialProfileApiService.getSerialProfiles()),
            takeUntil(this.destroy$),
          )
          .subscribe({
            next: (response) => {
              // Update local state with fresh data from API, sorted by name
              const sortedData = [...response.data].sort((a, b) =>
                a.name.localeCompare(b.name),
              );
              this.profiles.set(sortedData);
              this.updateFilteredProfiles();
              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant(
                  'common.messages.success',
                ),
                detail: this.translateService.instant(
                  'settings.serial_profile.messages.create_success',
                ),
              });
            },
            error: (error) => {
              console.error('Error creating profile:', error);
              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('common.messages.error'),
                detail: this.translateService.instant(
                  'settings.serial_profile.messages.save_error',
                ),
              });
            },
          });
      }
    });
  }

  /**
   * Open edit profile dialog
   */
  onEdit(profile: SerialProfile): void {
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.dialogRef = this.dialogService.open(SerialProfileDialogComponent, {
      header: this.translateService.instant(
        'settings.serial_profile.dialog.edit_title',
      ),
      width: '600px',
      modal: true,
      closable: true,
      data: { profile, onConfirm: this.onConfirm.bind(this) },
    });

    this.dialogRef.onClose.subscribe((result: SerialProfileFormData) => {
      if (result) {
        const statusChanged = profile.status !== result.status;
        const dataChanged =
          profile.name !== result.name ||
          profile.description !== result.description ||
          profile.lotRequired !== result.lotRequired ||
          profile.expiryRequired !== result.expiryRequired ||
          profile.serialRequired !== result.serialRequired;

        let updateObservable: Observable<void>;

        if (dataChanged) {
          // Update data first
          const profileData: Partial<SerialProfile> = {
            name: result.name,
            description: result.description,
            lotRequired: result.lotRequired,
            expiryRequired: result.expiryRequired,
            serialRequired: result.serialRequired,
          };
          updateObservable = this.serialProfileApiService
            .updateSerialProfile(profile.id, profileData)
            .pipe(map(() => undefined));
          // If status also changed, chain the status update
          if (statusChanged) {
            updateObservable = updateObservable.pipe(
              switchMap(() =>
                this.serialProfileApiService
                  .updateSerialProfileStatus(profile.id, result.status)
                  .pipe(map(() => undefined)),
              ),
            );
          }
        } else if (statusChanged) {
          // Only status changed
          updateObservable = this.serialProfileApiService
            .updateSerialProfileStatus(profile.id, result.status)
            .pipe(map(() => undefined));
        } else {
          // No changes
          updateObservable = of(undefined);
        }

        updateObservable
          .pipe(
            switchMap(() => this.serialProfileApiService.getSerialProfiles()),
            takeUntil(this.destroy$),
          )
          .subscribe({
            next: (response) => {
              // Update local state with fresh data from API, sorted by name
              const sortedData = [...response.data].sort((a, b) =>
                a.name.localeCompare(b.name),
              );
              this.profiles.set(sortedData);
              this.updateFilteredProfiles();
              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant(
                  'common.messages.success',
                ),
                detail: this.translateService.instant(
                  'settings.serial_profile.messages.update_success',
                ),
              });
            },
            error: (error) => {
              console.error('Error updating profile:', error);
              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('common.messages.error'),
                detail: this.translateService.instant(
                  'settings.serial_profile.messages.save_error',
                ),
              });
            },
          });
      }
    });
  }

  /**
   * Get status display text
   */
  getStatusDisplayText(status: SerialProfileStatus): string {
    switch (status) {
      case 'ACTIVE':
        return this.translateService.instant('common.table.status.active');
      case 'INACTIVE':
        return this.translateService.instant('common.table.status.inactive');
      case 'ARCHIVED':
        return this.translateService.instant(
          'settings.serial_profile.status.archived',
        );
      default:
        return status;
    }
  }

  /**
   * Get status severity
   */
  getStatusSeverity(status: SerialProfileStatus): string {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'danger';
      case 'ARCHIVED':
        return 'warning';
      default:
        return 'secondary';
    }
  }

  /**
   * Confirmation dialog state
   */
  showConfirmationDialog = signal(false);
  confirmationTitle = signal('');
  confirmationMessage = signal('');
  confirmationButtons = signal<ConfirmationButton[]>([]);

  /**
   * Handle confirmation dialog actions
   */
  onConfirmationDialogAction(action: string): void {
    this.confirmResult$.next(action === 'confirm');
    this.showConfirmationDialog.set(false);
  }

  private shouldConfirmArchive(dialog: SerialProfileDialogComponent): boolean {
    return (
      dialog.isEditMode &&
      dialog.originalStatus !== 'ARCHIVED' &&
      dialog.formData.status === 'ARCHIVED'
    );
  }

  private showArchiveConfirmation(data: SerialProfileFormData) {
    this.confirmationTitle.set(
      this.translateService.instant(
        'settings.serial_profile.dialog.archive_title',
      ),
    );
    this.confirmationMessage.set(
      this.translateService.instant(
        'settings.serial_profile.messages.confirm_archive',
        { name: data.name },
      ),
    );
    this.confirmationButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.confirm'),
        action: 'confirm',
        severity: 'danger',
      },
    ]);
    this.showConfirmationDialog.set(true);
  }
}
